class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return

      observer.disconnect()
      this.#loadRecommendations()
    },
    { rootMargin: '0px 0px 400px 0px' }
  )

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue

      // All other attribute changes trigger a reload
      this.#loadRecommendations()
      break
    }
  })

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {}

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null

  /**
   * The Keen Slider instance
   * @type {any | null}
   */
  #sliderInstance = null

  connectedCallback() {
    this.#intersectionObserver.observe(this)
    this.#mutationObserver.observe(this, { attributes: true })
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } =
      this.dataset
    const id = this.id

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required')
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`))
          }
          return
        }

        const html = document.createElement('div')
        html.innerHTML = result.data || ''
        const recommendations = html.querySelector(
          `product-recommendations[id="${id}"]`
        )

        if (
          recommendations?.innerHTML &&
          recommendations.innerHTML.trim().length
        ) {
          this.dataset.recommendationsPerformed = 'true'
          this.innerHTML = recommendations.innerHTML

          // Initialize Keen Slider after content is loaded
          this.#initializeSlider()
        } else {
          this.#handleError(new Error('No recommendations available'))
        }
      })
      .catch((e) => {
        this.#handleError(e)
      })
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`

    const cachedResponse = this.#cachedRecommendations[url]
    if (cachedResponse) {
      return { success: true, data: cachedResponse }
    }

    this.#activeFetch?.abort()
    this.#activeFetch = new AbortController()

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal })
      if (!response.ok) {
        return { success: false, status: response.status }
      }

      const text = await response.text()
      this.#cachedRecommendations[url] = text
      return { success: true, data: text }
    } finally {
      this.#activeFetch = null
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error)
    this.classList.add('hidden')
    this.dataset.error = 'Error loading product recommendations'
  }

  /**
   * Initialize Keen Slider after recommendations are loaded
   */
  #initializeSlider() {
    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      const slider = this.querySelector('#my-keen-slider')
      // @ts-ignore - KeenSlider is loaded from CDN
      const KeenSlider = window.KeenSlider
      if (slider && KeenSlider) {
        this.#sliderInstance = new KeenSlider(slider, {
          loop: true,
          slides: {
            perView: 4,
            spacing: 24,
            centered: true
            // slidesOffsetBefore: 24,
            // slidesOffsetAfter: 24
          },
          breakpoints: {
            '(max-width: 768px)': {
              slides: {
                perView: 2.5,
                spacing: 24
              }
            },
            '(max-width: 1024px)': {
              slides: {
                perView: 1.5,
                spacing: 12
              }
            }
          }
        })

        // Wire up navigation buttons (no disabled/dulling state)
        const prevButton = this.querySelector('.slider-button-prev')
        const nextButton = this.querySelector('.slider-button-next')

        if (prevButton) {
          prevButton.addEventListener('click', () => {
            this.#sliderInstance && this.#sliderInstance.prev()
          })
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            this.#sliderInstance && this.#sliderInstance.next()
          })
        }
      }
    }, 0)
  }

  /**
   * Cleanup when element is disconnected
   */
  disconnectedCallback() {
    this.#sliderInstance?.destroy()
    this.#activeFetch?.abort()
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations)
}
